import React, { useState } from 'react';
import './App.css';
import { apiBase } from './config';

// The c6-server endpoint, derived from the environment picked in config.js, so a
// production build points at App Runner and `npm start` at a local server without
// anything here being edited.
const PREDICT_URL = `${apiBase}/predict`;

const ResultBubble = ({ text }) => {
  return (
    <div className="result-bubble-container">
      <div className="result-bubble">
        <p className="result-bubble-text">{text}</p>
      </div>
    </div>
  );
};

function App() {
  const [image, setImage] = useState(null);
  const [result, setResult] = useState(null);
  const [resultText, setResultText] = useState(null);
  const [error, setError] = useState(null);   // { message, hint } from the server

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setImage(e.target.result);
      };
      reader.readAsDataURL(file);
      setResult(null);
      setResultText(null);
      setError(null);
    }
  };

  const handleSubmit = async () => {
    if (!image) return;

    const formData = new FormData();
    const blob = dataURLtoBlob(image);
    // The name is a placeholder with no extension on purpose: the blob carries the
    // file's real MIME type, and c6-server identifies the format from the image
    // bytes regardless, so a hardcoded ".jpg" here would only be misleading now
    // that any common image format is accepted.
    formData.append('file', blob, 'upload');

    setError(null);

    try {
      const response = await fetch(PREDICT_URL, {
        method: 'POST',
        body: formData,
      });

      const data = await response.json().catch(() => null);

      // c6-server explains anything it can as { detail: { message, hint, id } }: a
      // 400 for an upload it can't read as an image, a 413 for one over 20 MB, a 422
      // when the image isn't a cookie, a 429 when this client has made too many
      // requests in the last hour, a 503 when the models aren't loaded. Only the 422
      // needs special handling below -- the rest are shown by the generic branch,
      // which is why a 429 needs no code here beyond this note.
      const detail = data && data.detail;

      // A 422 is the classifier's verdict on the image, not a failure: the server
      // gates on it and never counts chips for a non-cookie. Show the normal result
      // dialog (which already says "That is not a cookie!"), not an error.
      if (response.status === 422 && detail && detail.prediction_text) {
        setResult(0);
        setResultText(detail.prediction_text);
        return;
      }

      if (!response.ok) {
        // `message` is written to be shown as-is. Anything else (FastAPI's own
        // validation 422, whose detail is an array; a proxy error page) has no
        // message, so fall back to the generic line.
        console.error('Predict failed:', response.status, detail);
        setError({
          message: (detail && detail.message)
            || 'The count regretfully cannot be made at this time.',
          hint: detail && detail.hint,
        });
        return;
      }

      setResult(data.prediction);
      setResultText(data.prediction_text);
    } catch (e) {
      // Never reached the server, or it answered with something unparseable.
      console.error('Error:', e);
      setError({
        message: 'The count regretfully cannot be made at this time.',
        hint: 'The counting service could not be reached.',
      });
    }
  };

  function dataURLtoBlob(dataurl) {
    const arr = dataurl.split(',');
    const mime = arr[0].match(/:(.*?);/)[1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    return new Blob([u8arr], { type: mime });
  }

  const getPredictionMessage = () => {
    if (result === null || resultText === null) return null;
    if (resultText === "Cookie") {
      return `There are ${Math.round(result)} chocolate chips in the cookie.`;
    } else if (resultText === "Uncertain") {
      // The classifier came back below its confidence threshold, so the server
      // refused to count (422) rather than commit either way.
      return "The count cannot tell whether that is a cookie.";
    } else {
      return "That is not a cookie!";
    }
  };

  const getErrorMessage = () =>
    error && (error.hint ? `${error.message} ${error.hint}` : error.message);

  return (
    <div className="App">
      <div className="app-body">
        <h1>Count Chocolate II</h1>

        <div className="controls-container">
          <p>Upload an image of a chocolate chip cookie and the count will count the chocolate chips in it.<br/><br/>Common image formats are supported. For best results, use a clear, well-lit image of a single cookie. Do not upload images of non-cookies, the count will not tolerate it.</p>
          <input type="file" accept="image/*" onChange={handleImageUpload} />
          <button onClick={handleSubmit}>Count chocolate chips</button>
        </div>

        {error !== null && <ResultBubble text={getErrorMessage()} />}

        {result !== null && resultText !== null && (
          <ResultBubble text={getPredictionMessage()} />
        )}

        {result !== null && resultText !== null && (
          <div className="image-container">
            <img src={image} alt="Uploaded" className="image" />
          </div>
        )}

        <div className="copyright">© 2024-{new Date().getFullYear()} S.K.J. All rights reserved. v2.1</div>
      </div>
    </div>
  );
}

export default App;
